import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  ErrorHandler,
  isDevMode,
} from '@angular/core';
import { TenantContextService } from '@bodyanalytics/calendar-tenant-context-domain';
import { ENV } from '@bodyanalytics/app-config';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAuth0, authHttpInterceptorFn } from '@auth0/auth0-angular';
import {
  GlobalErrorHandler,
  httpErrorInterceptor,
  ngZoneReentryInterceptor,
  provideSentryErrorReporting,
  withMaintenanceMode,
} from '@bodyanalytics/ui-core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { NgxStripeModule } from 'ngx-stripe';
import { ToastrModule } from 'ngx-toastr';

import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENV, useValue: environment },
    provideAnimations(),
    // Runtime multi-tenancy: when the portal is opened through a tenant mount
    // (`/t/:slug`, legacy `?t=<slug>`), resolve the tenant's branding BEFORE
    // first render via the shared BrandingService (native fetch — no Auth0/
    // HttpClient interceptor deadlock, same posture as the admin portal's
    // initializer). No slug → resolves immediately with the stock AppliedBioX
    // theme: zero behavior change for every existing link.
    provideAppInitializer(() => inject(TenantContextService).initialize()),
    provideAuth0({
      ...environment.auth0,
      httpInterceptor: {
        allowedList: [
          `${environment.api}/register`,
          `${environment.api}/checkout-api/create-payment-intent`,
          `${environment.api}/manage-booking-api/cancel-booking`,
          `${environment.api}/manage-booking-api/finalize-booking`,
          `${environment.api}/manage-booking-api/process-booking`,
          `${environment.api}/manage-booking-api/process-booking-credit`,
          `${environment.api}/manage-booking-api/process-booking-flow`,
          `${environment.api}/manage-booking-api/details/*`,
          `${environment.api}/manage-booking-api/reschedule-booking`,
          `${environment.api}/manage-booking-api/search/*`,
          `${environment.api}/manage-orders-api/details/*`,
          `${environment.api}/manage-orders-api/confirmation/*`,
          `${environment.api}/manage-reservations-api`,
        ],
      },
    }),
    // ngZoneReentryInterceptor is registered FIRST (outermost) so it is the
    // last thing a response passes through before reaching app code — see
    // its doc comment / #1811 for why the auth token step can escape NgZone.
    provideHttpClient(withInterceptors([ngZoneReentryInterceptor, authHttpInterceptorFn, httpErrorInterceptor])),
    // App-wide ErrorHandler — surfaces uncaught runtime errors as an accessible
    // toast instead of a silent half-rendered screen (no provider existed before).
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Initializes the Sentry SDK (#2191) — a no-op unless SENTRY_ENABLED_FRONTEND
    // + SENTRY_DSN_FRONTEND are set; GlobalErrorHandler.reportToSink() is the call site.
    provideSentryErrorReporting(environment),
    provideRouter(withMaintenanceMode(appRoutes, environment.maintenanceMode), withComponentInputBinding()),
    importProvidersFrom(
      StoreModule.forRoot(
        {},
        {
          metaReducers: [],
          runtimeChecks: {
            strictActionImmutability: true,
            strictStateImmutability: true,
          },
        },
      ),
      StoreRouterConnectingModule.forRoot(),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
      }),
      NgxGoogleAnalyticsModule.forRoot(environment.analyticsMeasurementId),
      NgxGoogleAnalyticsRouterModule,
      NgxStripeModule.forRoot(environment.stripePKey),
      ToastrModule.forRoot(),
    ),
  ],
};
